<template>
  <PageHeader :title="$t('ui.entities.appointmentManager')">
    <template #right>
      <div>
        <div
          v-if="dealershipsAllowed.length > 1"
          class="w-full flex items-center gap-2"
        >
          <Select
            v-model="dealershipsSelected"
            :options="dealershipsAllowed"
            :chips="dealershipsSelected.length == 1"
            mode="multiple"
            value-key="id"
            searchable
            label-key="name"
            placeholder="Select a Dealership"
            class="min-w-[250px]"
          ></Select>
          <Button
            label="Update"
            :is-loading="appointmentsApiRequest.isLoading.value"
            @click="onDealershipChange"
          />
        </div>
        <div
          v-else
          class="flex"
        >
          <Icon
            type="location-dot"
            class="mr-1"
          />
          <div>{{ dealershipsAllowed[0]?.name }}</div>
        </div>
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="appointmentsComputed"
      removable-sort
      sort-field="time"
      :sort-order="1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['customerName', 'dealershipName']"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-appointment-manager"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="appointmentsApiRequest.isLoading.value"
    >
      <template #empty>
        <Empty
          v-if="!appointmentsApiRequest.isLoading.value"
          :title="!dealershipsSelected ? 'No data' : `Select a dealership`"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <Column
        field="date"
        header="Date"
        :sortable="true"
      >
        <template #body="{ data }">
          <a
            class="link"
            :href="'/agreement/' + data.agreementId + '/appointments/' + data.id"
          >
            <div class="whitespace-nowrap">{{ dayjs(data.date).format('Do MMM YY H:mm') }}</div>
          </a>
        </template>
      </Column>

      <Column
        field="customerName"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.customerName }}
        </template>
      </Column>

      <Column
        field="dealershipID"
        header="Dealership"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div>{{ data.dealershipName }}</div>
        </template>
      </Column>

      <Column
        field="method"
        header="Method"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Icon
            v-if="data.method"
            v-tippy="$t(getDictionaryItem('appointmentMethod', data.method)?.localeKey)"
            :type="$t(getDictionaryItem('appointmentMethod', data.method)?.icon)"
          />
        </template>
      </Column>

      <Column
        field="actionStatus"
        header="Action"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <div v-if="data.actionStatus == 'unassigned'">
            <Select
              v-if="$can('assign_appointments')"
              :ref="data.id"
              v-model="data.assignTo"
              label-key="fullName"
              value-key="id"
              :options="getAssignableUsersForDealership(data.dealershipId)"
              searchable
              placeholder="Assign"
              class="w-full"
              @change="updateAssignedUser($event, data.id)"
            >
              <template #option="{ option }">
                <div class="flex items-center">
                  <Avatar
                    :url="option.profilePicUrl"
                    :text="option.initials"
                    size="xs"
                    class="mr-3"
                  />
                  {{ option.fullName }}
                </div>
              </template>
            </Select>
          </div>
          <div v-else>
            <Button
              v-if="$can('outcome_appointments')"
              label="Set Outcome"
              outlined
              :router-link="'/agreement/' + data.agreementId + '/appointments/' + data.id"
            />
          </div>
        </template>

        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="actionStatusOptions"
            filter
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="Any"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="view"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Button
            v-tippy="$t('ui.actions.view')"
            icon="eye"
            plain
            severity="secondary"
            @click="$router.push('/agreement/' + data.agreementId + '/appointments/' + data.id)"
          ></Button>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Icon from '@/components/icon/Icon.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Empty from '@/components/empty/Empty.vue'
// utilities
import { alertToast } from '@/utilities/notification'
import { isEmptyObject } from '@/utilities'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import Select from '@/components/forms/Select.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { getDictionaryItem } from '@/dictionary'

import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const dt = ref()

const route = useRoute()
const router = useRouter()

const appointmentsApiRequest = useApiRequest()
const authStore = useAuthStore()

const dealershipsSelected = ref([])
const dealershipsSelectedLS = JSON.parse(localStorage.getItem('appointment-manager-dealerships-selected'))
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)

if (dealershipsAllowed.value.length == 1) {
  dealershipsSelected.value = [dealershipsAllowed.value[0].id]
} else if (dealershipsSelectedLS) {
  dealershipsSelected.value = dealershipsSelectedLS
}

const actionStatusOptions = ref([
  {
    label: 'Unassigned',
    value: 'unassigned'
  },
  {
    label: 'No Outcome',
    value: 'no_outcome'
  }
])

const appointments = ref([])
const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actionStatus: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-appointment-manager')
  initFilters()
}

function setRouteFilters() {
  if (route.query && !isEmptyObject(route.query)) {
    clearFilters()
    for (const [key, value] of Object.entries(route.query)) {
      if (key === 'dealerships') {
        localStorage.setItem('appointment-manager-dealerships-selected', JSON.stringify(value.split(',').map(Number)))

        dealershipsSelected.value = value.split(',').map(Number)
      }
      if (key === 'actionStatus') {
        filters.value.actionStatus.value = value.split(',')
      }
    }
  }
  // Remove url query params  using router replace
  router.replace({ query: {} })
}

setRouteFilters()
onDealershipChange()

function fetchAppointments() {
  appointmentsApiRequest
    .send({
      endpoint: '/v1/appointments',
      params: {
        start: '2024-01-01',
        dealerships: dealershipsSelected.value.join(','),
        requireAction: true
      }
    })
    .then(response => {
      if (response.success) {
        appointments.value = response.data
        // Fetch assignable users for all unique dealerships in the appointments
        const uniqueDealershipIds = [...new Set(response.data.map(a => a.dealershipId))]
        uniqueDealershipIds.forEach(dealershipId => {
          fetchAssignableUsers(dealershipId)
        })
      } else {
        alertToast('Failed to fetch appointments', response.data?.message, 'error')
      }
    })
}

function onDealershipChange() {
  if (dealershipsSelected.value?.length) {
    fetchAppointments()
  } else {
    appointments.value = []
  }
  // Store selected dealerships in local storage
  localStorage.setItem('appointment-manager-dealerships-selected', JSON.stringify(dealershipsSelected.value))
}

const storedAssignableUsers = ref([])
const assignableUsersApiRequest = useApiRequest()
// Track which dealerships are currently being fetched to prevent duplicate requests
const fetchingDealerships = ref(new Set())

/**
 * Gets assignable users for a specific dealership
 * @param {number} dealershipId - The dealership ID
 * @returns {Array} Array of assignable users for the dealership
 */
function getAssignableUsersForDealership(dealershipId) {
  // If assignable users are already stored, return them
  const stored = storedAssignableUsers.value.find(d => d.dealershipId == dealershipId)
  if (stored) {
    return stored.assignableUsers
  }
  // Otherwise, fetch them (only if not already fetching)
  if (!fetchingDealerships.value.has(dealershipId)) {
    fetchAssignableUsers(dealershipId)
  }
  return []
}

/**
 * Fetches assignable users for a specific dealership and caches them
 * @param {number} dealershipId - The dealership ID
 */
function fetchAssignableUsers(dealershipId) {
  // Don't fetch if already stored or already fetching
  if (storedAssignableUsers.value.some(d => d.dealershipId == dealershipId)) {
    return
  }
  if (fetchingDealerships.value.has(dealershipId)) {
    return
  }

  // Mark as fetching
  fetchingDealerships.value.add(dealershipId)

  assignableUsersApiRequest
    .send({
      endpoint: '/v1/users?forAppointmentAssignment=true&userType=externalUsers&dealerships=' + dealershipId,
      method: 'GET'
    })
    .then(response => {
      if (response.data && response.data.length) {
        const users = response.data.filter(user => {
          return user.isActive === true
        })
        storedAssignableUsers.value.push({
          dealershipId: dealershipId,
          assignableUsers: users
        })
      }
    })
    .catch(() => {
      // Error handling is done by useApiRequest
    })
    .finally(() => {
      // Remove from fetching set when done
      fetchingDealerships.value.delete(dealershipId)
    })
}

const assignApiRequest = useApiRequest()
const updateSource = authStore.details.userType == 1 ? 'retain' : 'dealer'

const updateAssignedUser = (event, appointmentId) => {
  const dataToPost = {
    userToAssign: event,
    notifyUser: false,
    updatedBy: authStore.details.id,
    updateSource: updateSource
  }

  assignApiRequest
    .send({
      endpoint: '/v1/appointments/' + appointmentId + '/assign',
      method: 'PATCH',
      data: dataToPost
    })
    .then(response => {
      if (response.success) {
        appointments.value.find(a => a.id == appointmentId).assignedTo = event.value
        alertToast('Assigned', null, 'success')
      } else {
        alertToast('Failed to assign', response.data?.message, 'error')
      }
    })
}

const appointmentsComputed = computed(() => {
  return appointments.value.map(a => {
    return {
      ...a,
      actionStatus: a.assignedTo ? 'no_outcome' : 'unassigned',
      date: a.time,
      customerName: a.customerName
    }
  })
})
</script>

<style></style>
